/**
 * Sync-telemetry context helpers — the NO-PII envelope fields that come from
 * the device, not the caller: an anonymous rotated client id, the account-size
 * bucket, and the coarse network state.
 *
 * Kept in its own module (away from the reporter + guards) so the bucketing /
 * id-rotation logic is independently unit-testable and the reporter stays a
 * thin orchestrator.
 */

import { generateUuid7 } from "@/utils/uuid7";

/** localStorage key holding the rotated ANONYMOUS client id (NOT the user id). */
const CLIENT_ID_KEY = "syncTelemetryClientId";
/** localStorage key holding the epoch-ms the current client id was minted. */
const CLIENT_ID_MINTED_KEY = "syncTelemetryClientIdMintedAt";
/** Rotate the anonymous id periodically so it can't become a stable supercookie. */
const CLIENT_ID_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Account-size buckets — never the exact chat count (re-identifying at the tail). */
export type AccountSizeBucket = "0-100" | "101-1k" | "1k-10k" | "10k+";

/**
 * Network state snapshot. `online` is always present; `effectiveType` (e.g.
 * "4g") is best-effort from the Network Information API where the browser
 * exposes it (Chromium) and omitted elsewhere (Safari/Firefox).
 */
export interface NetworkState {
  online: boolean;
  effectiveType?: string;
}

/**
 * Get the persisted anonymous client id, generating + persisting one if absent
 * or expired. This is a RANDOM uuid, NOT the user id / username — it lets us
 * count distinct clients (for "events/client/day" sizing) without identifying
 * users, and it rotates on a TTL so it can't be used as a long-lived tracker.
 *
 * Defensive against a missing / throwing localStorage (private mode, quota):
 * falls back to an in-memory id for the session so telemetry still works and
 * the daily-cap key remains stable within the page lifetime.
 */
let memoryClientId: string | null = null;
export function getOrCreateClientId(): string {
  const now = Date.now();
  // NOTE: this function must distinguish a *throwing* storage (blocked Safari →
  // fall back to a stable per-session in-memory id) from a *missing key* (→ mint
  // and persist a fresh id). safeStorage collapses both into `null`, so it can't
  // preserve the stable-id guarantee here. The direct access below is already
  // fully wrapped in try/catch, so it is throw-safe — hence the targeted
  // eslint-disable rather than a safeStorage migration.
  try {
    /* eslint-disable no-restricted-globals */
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    const mintedRaw = localStorage.getItem(CLIENT_ID_MINTED_KEY);
    const minted = mintedRaw ? Number(mintedRaw) : 0;
    const expired = !Number.isFinite(minted) || now - minted > CLIENT_ID_TTL_MS;
    if (existing && !expired) {
      return existing;
    }
    const fresh = generateUuid7();
    localStorage.setItem(CLIENT_ID_KEY, fresh);
    localStorage.setItem(CLIENT_ID_MINTED_KEY, String(now));
    return fresh;
    /* eslint-enable no-restricted-globals */
  } catch {
    // localStorage unavailable — keep a stable per-session id in memory.
    memoryClientId ??= generateUuid7();
    return memoryClientId;
  }
}

/**
 * Bucket a local chat count into a coarse size band. Returns "0-100" when the
 * count is unknown (the conservative default — never the exact number). The
 * bands are inclusive at the top edge of the lower band:
 *   0..100 → "0-100", 101..1000 → "101-1k", 1001..10000 → "1k-10k", else "10k+".
 */
export function bucketChatCount(count: number | undefined): AccountSizeBucket {
  if (count == null || !Number.isFinite(count) || count <= 100) {
    return "0-100";
  }
  if (count <= 1_000) {
    return "101-1k";
  }
  if (count <= 10_000) {
    return "1k-10k";
  }
  return "10k+";
}

/**
 * Collect the coarse network state. `effectiveType` is read from the Network
 * Information API when available; it is a coarse class ("slow-2g".."4g"), never
 * an IP or carrier — safe to ship.
 */
export function collectNetworkState(): NetworkState {
  const online =
    typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
      ? navigator.onLine
      : true;
  let effectiveType: string | undefined;
  try {
    const conn = (
      navigator as unknown as {
        connection?: { effectiveType?: string };
      }
    ).connection;
    if (conn && typeof conn.effectiveType === "string") {
      effectiveType = conn.effectiveType;
    }
  } catch {
    /* Network Information API unavailable — omit. */
  }
  return effectiveType ? { online, effectiveType } : { online };
}
